/**
 * Walk it through: the whole room in the order a guest meets it, three steps.
 * The page, the room (the offer is handled there), the follow-ups. Each step is already made; the
 * host looks, changes any part by talking to it (or, for follow-ups, with the
 * flow's own controls), says "Looks right", and publishes at the end.
 *
 * Everything here is drawn from the board the engine holds (`host`), and every
 * change goes through it, so the save queue, its versions and the server's
 * verdicts stay exactly what they were.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { BOARD_CAPS } from "../../../shared/board-caps";
import type { BoardState } from "../../../shared/types";
import { Menu } from "../../components/ui/Menu";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import { cx } from "../../components/ui/cx";
import { useToast } from "../../components/ui/Toast";
import type { BoardHost } from "../host";
import { LiveStrip } from "../LiveStrip";
import { ChaptersBox, chapterCount, talkLength, useChapterTalk, type ChapterRow } from "../make/MakeChapters";
import { Editable } from "./Editable";
import { WalkFollow } from "./WalkFollow";
import { ViewSeg } from "./ViewSeg";
import { WalkPage } from "./WalkPage";
import { usePublishFacts } from "./publishFacts";
import { lostChanges, STEP_NAMES as NAMES, useWalk, WALK_STEPS, type WalkStep } from "./useWalk";
import { WalkRoom } from "./WalkRoom";
import "../../styles/make.css";
import "../../styles/walk.css";

export function WalkScreen({ state, host }: { state: BoardState; host: BoardHost }) {
  const w = useWalk(state, host);
  const toast = useToast();
  const { navigate } = w;
  const { step, at, go, looked, published, live, server, studio, base, minutes, titleCard, chaptersCard, roomName, rename, info, last, undoLast, remember } = w;
  const [asPhone, setAsPhone] = useState(false);
  const phone = asPhone;
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const [publishing, setPublishing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [startingOver, setStartingOver] = useState(false);
  const [splitting, setSplitting] = useState(false);

  // Why a ref: "Go to the offer" changes the step, and a new step starts with nothing open, so it asks for the offer's card here.
  const offerNext = useRef(false);
  // A different step starts with nothing open.
  useEffect(() => {
    setOpen(offerNext.current ? "ofwhen" : null);
    offerNext.current = false;
    host.select(null);
    // Why the shell's main area: it is what scrolls under the top bar, not the window.
    root.current?.closest(".main")?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
    // `host` changes every render; only the step matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Why: a card belongs to the part it opened by; a click anywhere else puts it away.
  useEffect(() => {
    if (!open) return;
    const was = open;
    const away = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element) || t.closest(".bw-card, [data-bw], .bw-whole, .bw-orow, .toast, .scrim, .ph-sheet-scrim, .as-scrim, #as-layer, .menu")) return;
      setTimeout(() => setOpen((o) => (o === was ? null : o)), 0);
    };
    document.addEventListener("click", away, true);
    return () => document.removeEventListener("click", away, true);
  }, [open]);

  const next = () => w.next(() => setPublishing(true));

  const nav = (
    <div className="bw-nav">
      {at > 0 ? (
        <button type="button" className="btn q" onClick={() => go(WALK_STEPS[at - 1])}>
          <Icon name="left" /> Back
        </button>
      ) : null}
      <button type="button" className={cx("btn p bw-next", looked.has(step) && "was")} onClick={next}>
        <Icon name="check" />
        <span>{at === WALK_STEPS.length - 1 ? (published ? "Looks right" : "Looks right, publish") : "Looks right"}</span>
        {at === WALK_STEPS.length - 1 ? null : <Icon name="right" />}
      </button>
    </div>
  );

  return (
    <div className={cx("bw-walk", phone && "bw-isphone")} data-step={step} ref={root}>
      <div className="bw-bar">
        <div className="bw-bar-in">
          <div className="bw-bl">
            <button type="button" className="ibtn bd" title="Back to Rooms" aria-label="Back to Rooms" onClick={() => navigate("/rooms")}>
              <Icon name="left" />
            </button>
            <div className="bw-title">
              <Editable
                as="div"
                className="bw-name"
                value={roomName}
                label="The room’s name"
                tip="Click to rename"
                name
                max={titleCard ? BOARD_CAPS.title : 60}
                onType={rename}
                onDone={(_, was) => remember(() => rename(was))}
              />
              {published ? (
                <span className="pill good">
                  <i />
                  Live
                </span>
              ) : (
                <span className="pill warn">Draft</span>
              )}
              <span className="bw-src">{live ? "A live room" : `From a recording${minutes ? ` · ${minutes} min` : ""}`}</span>
            </div>
          </div>
          <div className="bw-br">
            {last ? (
              <button type="button" className="btn q" title="Undo the last change" onClick={undoLast}>
                <Icon name="undo" /> Undo
              </button>
            ) : null}
            <Menu
              label="More"
              buttonClassName="bd"
              items={[
                { label: "Preview the page full size", icon: "eye", onSelect: () => setPreview(true) },
                {
                  label: "Copy the page’s address",
                  icon: "link",
                  onSelect: () => {
                    void navigator.clipboard?.writeText(host.liveUrl || `https://${base}/${state.slug}`).catch(() => undefined);
                    toast({ text: "Copied", icon: "copy" });
                  },
                },
                ...(published ? [{ label: "Unpublish", icon: "pause" as const, onSelect: () => host.unpublish() }] : []),
                "sep" as const,
                { label: live ? "Start over from your notes" : "Start over from the talk", icon: "refresh", onSelect: () => setStartingOver(true) },
              ]}
            />
            {published ? (
              <button
                type="button"
                className="btn p"
                onClick={() => {
                  void navigator.clipboard?.writeText(host.liveUrl).catch(() => undefined);
                  toast({ text: "Copied", icon: "copy" });
                }}
              >
                <Icon name="link" /> Copy link
              </button>
            ) : (
              <button type="button" className="btn p" onClick={() => setPublishing(true)}>
                <Icon name="send" /> Publish
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="bw-body">
        <nav className="bw-rail" aria-label="The steps">
          <div className="bw-rail-h">Walk it through</div>
          <ol className="bw-rl">
            {WALK_STEPS.map((k, i) => (
              <li key={k}>
                <button type="button" className={cx("bw-ri", looked.has(k) && "ok", step === k && "on")} title={`Step ${i + 1}. ${NAMES[k]}`} {...(step === k ? { "aria-current": "step" as const } : {})} onClick={() => go(k)}>
                  <span className="bw-rn">{looked.has(k) ? <Icon name="check" /> : i + 1}</span>
                  <span className="bw-rt">
                    <b>{NAMES[k]}</b>
                    {info[k] ? <em>{info[k]}</em> : null}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <div className="bw-rail-f">
            <div className="bw-meter">
              {WALK_STEPS.map((k) => (
                <i key={k} className={looked.has(k) ? "on" : ""} />
              ))}
            </div>
            <span>{looked.size >= WALK_STEPS.length ? "All done" : `${WALK_STEPS.filter((k) => looked.has(k)).length} of ${WALK_STEPS.length}`}</span>
          </div>
        </nav>
        <div className="bw-main">
          <section className="bw-stop" data-stop={step}>
            <header className="bw-sh">
              <div className="sp">
                <div className="bw-lab">
                  Step {at + 1} of {WALK_STEPS.length}
                </div>
                <h2 className="bw-h">{NAMES[step]}</h2>
              </div>
              {step === "page" ? null : <ViewSeg phone={phone} onPhone={setAsPhone} />}
              {nav}
            </header>
            {host.message ? (
              <div className="note warn bw-warn" role="status">
                <Icon name="info" />
                <div className="sp">{host.message}</div>
              </div>
            ) : null}
            {host.warnings.length ? (
              <div className="note warn bw-warn" role="status">
                <Icon name="info" />
                <div className="sp">{host.warnings[0]}</div>
                <button type="button" className="ibtn sm" aria-label="Dismiss" onClick={host.dismissWarnings}>
                  <Icon name="x" />
                </button>
              </div>
            ) : null}
            {step === "page" && server.live ? <LiveStrip host={host} live={server.live} /> : null}
            <div className="bw-real">
              {step === "page" ? (
                <WalkPage
                  studio={studio}
                  live={live}
                  phone={phone}
                  onPhone={setAsPhone}
                  base={base}
                  slug={state.slug}
                  onSlug={host.saveSlug}
                  open={open}
                  onOpen={setOpen}
                  onChapters={() => setSplitting(true)}
                  onTyped={remember}
                  onChapterName={(i, name) => {
                    if (!chaptersCard || chaptersCard.kind !== "chapters" || !chaptersCard.rows[i]) return;
                    const rows = chaptersCard.rows.map((r, k) => (k === i ? { ...r, title: name.slice(0, 80) } : r));
                    host.dispatch({ type: "edit", id: chaptersCard.id, patch: { rows } as never });
                  }}
                />
              ) : step === "followups" ? (
                <WalkFollow state={state} host={host} phone={phone} />
              ) : (
                <WalkRoom
                  state={state}
                  host={host}
                  phone={phone}
                  open={open}
                  onOpen={setOpen}
                  onTyped={remember}
                  title={roomName}
                />
              )}
            </div>
            <footer className="bw-sf">{nav}</footer>
          </section>
        </div>
      </div>
      <PublishSheet
        open={publishing}
        state={state}
        host={host}
        looked={looked}
        address={host.liveUrl || `${base}/${state.slug}`}
        onClose={() => setPublishing(false)}
        onGo={(k) => {
          setPublishing(false);
          go(k);
        }}
        onGoOffer={() => {
          setPublishing(false);
          if (step === "room") setOpen("ofwhen");
          else {
            offerNext.current = true;
            go("room");
          }
        }}
      />
      {splitting && chaptersCard && chaptersCard.kind === "chapters" ? (
        <ChaptersSheet
          webinarId={host.webinarId}
          rows={chaptersCard.rows}
          onClose={() => setSplitting(false)}
          onDone={(rows) => {
            setSplitting(false);
            host.dispatch({ type: "edit", id: chaptersCard.id, patch: { rows } as never });
            toast("Chapters saved. The page and the player list them.");
          }}
        />
      ) : null}
      <Sheet
        open={startingOver}
        onClose={() => setStartingOver(false)}
        title={live ? "Start over from your notes?" : "Start over from the talk?"}
        sub="We write a new board from the start. There’s no undo."
        footer={
          <>
            <button type="button" className="btn" onClick={() => setStartingOver(false)}>
              Keep my board
            </button>
            <button
              type="button"
              className="btn p"
              onClick={() => {
                setStartingOver(false);
                host.readAgain();
              }}
            >
              Start over
            </button>
          </>
        }
      >
        <div className="nbcost">
          {lostChanges(studio.threads).map(([n, words]) => (
            <div key={words}>
              <b>{n}</b>
              <span>{words}</span>
            </div>
          ))}
        </div>
      </Sheet>
      {preview && studio.doc ? (
        <Sheet open onClose={() => setPreview(false)} title="The page" wide>
          <iframe className="nbpv-frame nbpv-wide" title="The page, full size" srcDoc={studio.doc} sandbox="" />
        </Sheet>
      ) : null}
    </div>
  );
}

/** Split or join chapters from the walk: the same editor as step 6, on the talk, saved to the chapters card. */
function ChaptersSheet({ webinarId, rows: start, onClose, onDone }: { webinarId: string; rows: ChapterRow[]; onClose: () => void; onDone: (rows: ChapterRow[]) => void }) {
  const [rows, setRows] = useState(start);
  const { lines, media } = useChapterTalk(webinarId);
  return (
    <Sheet
      open
      onClose={onClose}
      title={<span className="nbchn">{chapterCount(rows, talkLength(media, lines, rows))}</span>}
      label="Chapters"
      footer={
        <button type="button" className="btn p" disabled={rows.length === 0} onClick={() => onDone(rows)}>
          Done
        </button>
      }
    >
      <ChaptersBox rows={rows} onRows={setRows} lines={lines} media={media} />
    </Sheet>
  );
}

/** Publish: what is left to look at, what blocks it, then the page is live. */
function PublishSheet({
  open,
  state,
  host,
  looked,
  address,
  onClose,
  onGo,
  onGoOffer,
}: {
  open: boolean;
  state: BoardState;
  host: BoardHost;
  looked: ReadonlySet<string>;
  address: string;
  onClose: () => void;
  onGo: (k: WalkStep) => void;
  onGoOffer: () => void;
}) {
  const toast = useToast();
  const done = host.publishState === "done";
  const { blocks, waiting, seen, title, canEdit } = usePublishFacts(state, host, looked);
  const link = address.replace(/^https?:\/\//, "");
  if (done) {
    const href = address.startsWith("http") ? address : `https://${address}`;
    const copy = () => {
      void navigator.clipboard?.writeText(href).catch(() => undefined);
      toast({ text: "Copied", icon: "copy" });
    };
    return (
      <Sheet open={open} onClose={onClose} title="The page is live" label="The page is live">
        <div className="nbdone">
          <div className="bigck">
            <Icon name="check" />
          </div>
          <span className="pill good mt16">
            <i />
            Live
          </span>
          <h3>The page is live.</h3>
          <div className="copyv mt16">
            <span>{link}</span>
            <button type="button" className="ibtn" aria-label="Copy the link" onClick={copy}>
              <Icon name="copy" />
            </button>
          </div>
          <div className="row g8 mt20 nbdone-a">
            <button type="button" className="btn" onClick={onClose}>
              Back to the room
            </button>
            <a className="btn" href={href} target="_blank" rel="noreferrer">
              <Icon name="ext" /> Open
            </a>
            <button type="button" className="btn p" onClick={copy}>
              <Icon name="link" /> Copy link
            </button>
          </div>
          <Link className="link mt16" to="/rooms">
            See it in Rooms <Icon name="right" />
          </Link>
        </div>
      </Sheet>
    );
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Publish ${title}?`}
      sub={`It goes live at ${link}.`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Not yet
          </button>
          <button type="button" className="btn p" disabled={blocks.length > 0 || host.publishState === "sending"} onClick={host.publish}>
            <Icon name="send" /> {host.publishState === "sending" ? "Publishing…" : "Publish"}
          </button>
        </>
      }
    >
      <div className="eyebrow wine">{blocks.length ? "One thing first" : seen === WALK_STEPS.length ? "Every step looks right" : `${seen} of ${WALK_STEPS.length} steps looked at`}</div>
      <div className="nbcheck bw-check mt12">
        {blocks.map((b) => (
          <div className="nbck no" key={b}>
            <span className="o">
              <Icon name="info" />
            </span>
            <div>
              <b>{b}</b>
              {/offer/i.test(b) ? (
                <span>
                  <button type="button" className="link" onClick={onGoOffer}>
                    Go to the offer
                  </button>
                </span>
              ) : null}
            </div>
          </div>
        ))}
        {WALK_STEPS.map((k, i) => (
          <div className={cx("nbck", looked.has(k) ? "ok" : "wait")} key={k}>
            <span className="o">
              <Icon name={looked.has(k) ? "check" : "eye"} />
            </span>
            <div>
              <b>
                {i + 1} · {NAMES[k]}
              </b>
              {looked.has(k) ? null : (
                <span>
                  <button type="button" className="link" onClick={() => onGo(k)}>
                    Look now
                  </button>
                </span>
              )}
            </div>
          </div>
        ))}
        {waiting ? (
          <div className="nbck wait">
            <span className="o">
              <Icon name="mail" />
            </span>
            <div>
              <b>
                {waiting} {waiting === 1 ? "email waits" : "emails wait"} for your email
              </b>
              {canEdit ? (
                <span>
                  <Link className="link" to="/setup/email">
                    Set up email
                  </Link>
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      {host.publishError ? (
        <p className="note warn mt16" role="alert">
          <Icon name="info" />
          <span>{host.publishError}</span>
        </p>
      ) : null}
    </Sheet>
  );
}
